/**
 * FROZEN ARTWORK → GRID overlay (React/SVG), detail LOD only (#15 §22–§24).
 *
 * Renders each legitimately placed Frozen Artwork from its IMMUTABLE canonical
 * content at its CURRENT canonical Placement. Only mounted at the detail LOD.
 * Clicking an Artwork emits a window event the shell listens to (selection).
 *
 * IN-CONTEXT PLACEMENT EDIT: when the shared placementEditStore has an active
 * session for an artwork, that piece is rendered at the LIVE (unsaved) anchor /
 * rotation / opacity and becomes DRAGGABLE directly on the real Grid. The
 * right-side PlacementControls owns rotation/opacity/commit.
 */
import { memo, useEffect, useRef, useState } from "react";

import type { PixelContent, PublicPlacement } from "../lib/artwork-api";
import type { Camera } from "./camera";
import { worldToScreen, screenToWorld } from "./camera";
import { placementEditStore, type PlacementEditState } from "../ui/artwork/placementEditStore";
import { usePlacementEditState } from "../ui/artwork/usePlacementEdit";

export const ARTWORK_SELECT_EVENT = "grid:artwork-select";
export const ARTWORK_DESELECT_EVENT = "grid:artwork-deselect";

const PULSE_CSS = `
@keyframes gridArtPulse {
  0%   { opacity: 0.10; }
  50%  { opacity: 0.55; }
  100% { opacity: 0.10; }
}
.grid-art-halo { animation: gridArtPulse 2.4s ease-in-out 3 forwards; }
@media (prefers-reduced-motion: reduce) { .grid-art-halo { animation: none; opacity: 0.12; } }
`;

export function PlacedArtworkCanvas({
  content, width, height, boxW, boxH,
}: { content: PixelContent; width: number; height: number; boxW: number; boxH: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const transparentIdx = content.palette.length;
    let cell = 0;
    const total = width * height;
    for (const run of content.rle) {
      const sym = run[0];
      const count = run[1];
      for (let k = 0; k < count && cell < total; k++, cell++) {
        if (sym !== transparentIdx) {
          const colour = content.palette[sym];
          if (colour) {
            ctx.fillStyle = colour;
            ctx.fillRect(cell % width, Math.floor(cell / width), 1, 1);
          }
        }
      }
    }
  }, [content, width, height]);
  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{ width: boxW, height: boxH, imageRendering: "pixelated", display: "block" }}
    />
  );
}

const PlacedArtwork = memo(function PlacedArtwork({
  p, camera, hovered, selected, edit, onHover, onSelect,
}: {
  p: PublicPlacement; camera: Camera;
  hovered: boolean; selected: boolean;
  edit: PlacementEditState | null; // live edit override for THIS artwork (or null)
  onHover: (id: string | null) => void;
  onSelect: (p: PublicPlacement) => void;
}) {
  const dragRef = useRef<{ offx: number; offy: number } | null>(null);
  const editing = !!edit;

  const ax = edit ? edit.ax : p.anchor_x;
  const ay = edit ? edit.ay : p.anchor_y;
  const deg = edit ? edit.rotDeg : (p.rotation_deg ?? 0);
  const emptyOpacity = edit ? edit.opacityPct / 100 : (p.empty_opacity_bps ?? 0) / 10000;
  const bg = edit ? edit.bg : p.background;

  // NATURAL scale — the artwork zooms in/out proportionally to its own size
  // and the grid (no forced minimum). Anchor X/Y stay the backend authority.
  const anchor = worldToScreen(camera, ax, ay);
  const w = Math.max(1, p.width * camera.scale);
  const h = Math.max(1, p.height * camera.scale);
  const sx = anchor.x;
  const sy = anchor.y;
  const cx = sx + w / 2;
  const cy = sy + h / 2;
  const transform = deg ? `rotate(${deg} ${cx} ${cy})` : undefined;
  const pad = Math.max(3, Math.min(10, w * 0.12));
  const teal = "#2DB6F0";
  const showLabel = w >= 34 && !selected && !editing;

  const svgLocal = (e: React.PointerEvent) => {
    const svg = (e.currentTarget as SVGGElement).ownerSVGElement;
    const r = svg!.getBoundingClientRect();
    return screenToWorld(camera, e.clientX - r.left, e.clientY - r.top);
  };
  const onDown = (e: React.PointerEvent) => {
    if (!editing) return;
    e.stopPropagation();
    (e.currentTarget as SVGGElement).setPointerCapture?.(e.pointerId);
    const wpt = svgLocal(e);
    dragRef.current = { offx: wpt.x - ax, offy: wpt.y - ay };
  };
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !editing) return;
    const wpt = svgLocal(e);
    const nx = Math.max(0, Math.min(1000 - p.width, Math.round(wpt.x - dragRef.current.offx)));
    const ny = Math.max(0, Math.min(1000 - p.height, Math.round(wpt.y - dragRef.current.offy)));
    placementEditStore.update({ ax: nx, ay: ny });
  };
  const onUp = () => { dragRef.current = null; };

  const ringColour = editing ? (edit.valid ? "#34d399" : "#f87171") : (selected ? teal : "rgba(45, 182, 240,0.35)");

  return (
    <g
      data-testid={`grid-artwork-${p.artwork_id}`}
      data-revision={p.revision}
      data-selected={selected ? "true" : "false"}
      data-editing={editing ? "true" : "false"}
      transform={transform}
      style={{ pointerEvents: "auto", cursor: editing ? "move" : "pointer", touchAction: "none" }}
      onPointerEnter={() => onHover(p.artwork_id)}
      onPointerLeave={() => onHover(null)}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onClick={(e) => { e.stopPropagation(); if (!editing) onSelect(p); }}
    >
      {!selected && !editing && (
        <rect
          className="grid-art-halo"
          x={sx - pad} y={sy - pad} width={w + pad * 2} height={h + pad * 2}
          rx={Math.min(6, pad)} fill="none"
          stroke={teal} strokeWidth={hovered ? 2.5 : 1.5}
          style={{ opacity: hovered ? 0.7 : undefined, filter: `drop-shadow(0 0 ${hovered ? 8 : 4}px ${teal})` }}
        />
      )}
      {bg && (
        <rect x={sx} y={sy} width={w} height={h} fill={bg} opacity={emptyOpacity} />
      )}
      <foreignObject x={sx} y={sy} width={w} height={h}>
        <PlacedArtworkCanvas content={p.content} width={p.width} height={p.height} boxW={w} boxH={h} />
      </foreignObject>
      <rect
        x={sx} y={sy} width={w} height={h}
        fill="none"
        stroke={ringColour}
        strokeWidth={selected || editing ? 2 : 1}
        strokeDasharray={editing ? "6 4" : undefined}
      />
      {showLabel && (
        <g style={{ pointerEvents: "none" }}>
          <rect x={sx} y={Math.max(0, sy - 14)} width={Math.min(w, 92)} height={12} rx={2}
            fill="rgba(6,10,16,0.6)" />
          <text x={sx + 3} y={Math.max(0, sy - 14) + 9}
            fontFamily="ui-monospace, monospace" fontSize={8}
            fill="rgba(45, 182, 240,0.85)" letterSpacing="0.12em">FROZEN ARTWORK</text>
        </g>
      )}
    </g>
  );
});

export const GridArtworkLayer = memo(function GridArtworkLayer({
  camera, items,
}: { camera: Camera | null; items: PublicPlacement[] }) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const edit = usePlacementEditState();

  useEffect(() => {
    const clear = () => setSelectedId(null);
    window.addEventListener(ARTWORK_DESELECT_EVENT, clear);
    return () => window.removeEventListener(ARTWORK_DESELECT_EVENT, clear);
  }, []);

  // While an edit session is active, GUARANTEE the edited piece renders even
  // if the viewport-bounded discovery didn't include it (area cap / overview).
  const renderItems = [...items];
  if (edit && edit.content && !renderItems.some((p) => p.artwork_id === edit.artworkId)) {
    renderItems.push({
      placement_id: `edit-${edit.artworkId}`,
      artwork_id: edit.artworkId,
      owner: "",
      anchor_x: edit.ax, anchor_y: edit.ay,
      width: edit.width, height: edit.height,
      rotation_millideg: edit.rotDeg * 1000, rotation_deg: edit.rotDeg,
      background: edit.bg ?? null, empty_opacity_bps: edit.opacityPct * 100,
      revision: 0, active: true,
      content_hash: "", content_version: 0,
      content: edit.content, title: null, creator: "",
    });
  }

  if (!camera || renderItems.length === 0) return null;

  const select = (p: PublicPlacement) => {
    setSelectedId(p.artwork_id);
    window.dispatchEvent(new CustomEvent(ARTWORK_SELECT_EVENT, {
      detail: { artworkId: p.artwork_id, anchorX: p.anchor_x, anchorY: p.anchor_y },
    }));
  };

  return (
    <svg
      data-testid="grid-artwork-layer"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "hidden" }}
    >
      <style>{PULSE_CSS}</style>
      {renderItems.map((p) => (
        <PlacedArtwork
          key={p.placement_id}
          p={p}
          camera={camera}
          hovered={hoveredId === p.artwork_id}
          selected={selectedId === p.artwork_id}
          edit={edit && edit.artworkId === p.artwork_id ? edit : null}
          onHover={setHoveredId}
          onSelect={select}
        />
      ))}
    </svg>
  );
});
